import { resolveEtlNodeFromDocument } from "./facet-etl-document-helpers";
import type { AxisSortValue } from "./facet-table-sort-helpers";
import { isMatrixShapedOp } from "./facet-table-sort-helpers";

function pivotOpFromDocument(etlDoc: Record<string, unknown>): Record<string, unknown> | null {
    const etlNode = resolveEtlNodeFromDocument(etlDoc);
    if (!etlNode || !Array.isArray(etlNode.ops)) return null;
    for (let i = etlNode.ops.length - 1; i >= 0; i--) {
        const op = etlNode.ops[i];
        if (op != null && typeof op === "object") {
            const type = (op as Record<string, unknown>).type;
            if (type === "matrix" || type === "pivot") return op as Record<string, unknown>;
        }
    }
    return null;
}

function parseAxisSortFromRaw(raw: unknown): AxisSortValue | null {
    if (raw == null) return null;
    if (Array.isArray(raw)) return raw.length > 0 ? raw.map((x) => String(x ?? "")) : null;
    if (typeof raw === "object" && Object.keys(raw as object).length > 0) {
        return raw as Record<string, number>;
    }
    return null;
}

/** Row sort from pivot or matrix op (any table shape). */
export function getEtlRowSort(etlDoc: Record<string, unknown>): AxisSortValue | null {
    const op = pivotOpFromDocument(etlDoc);
    if (!op) return null;
    return parseAxisSortFromRaw(op.row_sort);
}

export function getPivotRowSort(etlDoc: Record<string, unknown>): AxisSortValue | null {
    const op = pivotOpFromDocument(etlDoc);
    if (!op || isMatrixShapedOp(op)) return null;
    return parseAxisSortFromRaw(op.row_sort);
}

function rankForLabel(label: string, rankMap: Record<string, number>): number {
    const candidates = [label, label.trim(), label.toLowerCase(), label.trim().toLowerCase()];
    for (const candidate of candidates) {
        if (candidate in rankMap) return rankMap[candidate];
    }
    const normalized = label.trim().toLowerCase();
    for (const [key, value] of Object.entries(rankMap)) {
        if (key.trim().toLowerCase() === normalized) return value;
    }
    return 1_000_000;
}

function rankMapFromSort(rowSort: AxisSortValue): Record<string, number> {
    if (Array.isArray(rowSort)) {
        const map: Record<string, number> = {};
        rowSort.forEach((label, idx) => {
            map[String(label ?? "")] = idx;
        });
        return map;
    }
    return rowSort;
}

export function applyRowSortToFacetResult(
    facetResult: Record<string, unknown>,
    rowSort: AxisSortValue
): Record<string, unknown> {
    const rows = facetResult.rows;
    if (!Array.isArray(rows)) return facetResult;
    const rankMap = rankMapFromSort(rowSort);
    const indexed = rows
        .map((row, index) => ({ row, index }))
        .filter((item) => Array.isArray(item.row));
    if (indexed.length === 0) return facetResult;

    indexed.sort((a, b) => {
        const aRank = rankForLabel(String((a.row as unknown[])[0] ?? ""), rankMap);
        const bRank = rankForLabel(String((b.row as unknown[])[0] ?? ""), rankMap);
        return aRank !== bRank ? aRank - bRank : a.index - b.index;
    });

    const arrayRows = new Set(indexed.map((item) => item.row));
    const tail = rows.filter((row) => !arrayRows.has(row));
    return { ...facetResult, rows: [...indexed.map((item) => item.row), ...tail] };
}
